import {AlertTypes, apiClient, Grid, IAlertData, setAlert, Paper, Typography, Skeleton, useTheme} from "@leap4/frontend";
import DataPointTagsTable from "./components/dataPointTagsTable";
import {getDefaultTimeDisplay, indexToTimeInterval} from "../common/charts/charts";
import ComparisonOverviewGraph, { IOverviewGraphResponse } from "../common/ComparisonGraph";
import {OverviewGeneralInfo} from "../common/OverviewGeneralInfo/OverviewGeneralInfo";
import {ReportHeaderWithProps} from "./components/DatapointReportHeader";
import {MetricsComparison} from "../../shared/MetricsComparison/MetricsComparison.container";
import {IDataPointOverviewResult} from "./interfaces";
import {metricsComparisonKeyMap} from "../../shared/MetricsComparison/MetricsComparisonHelpers";
import React, {useEffect, useState} from "react";
import {RootState} from "../../../interfaces/root-state.interface";
import {setIsLoading} from "../../../store/actions/dateRange.actions";
import {connect, ConnectedProps} from "react-redux";
import {IDateRangeGState, ILoadingComponents} from "../../../interfaces/datesGlobalState.interface";
import {useParams} from "react-router-dom";
import {useAppDispatch, useRefreshInterval} from "../../../store/hooks";
import {ComparisonState, dataPointsUrl} from "../../../config/generals";
import {PredictiveGraphContainer} from "../common/PredictiveGraph/PredictiveGraph.container";
import HomeTopTagsTable from "../home/components/topCombosTable/HomeTopTagsTable";
import {LastUpdatedAt} from "../common/LastUpdatedAt/LastUpdatedAt";
import moment from "moment";
import {checkIfDateIsInComparisonOptions, getComparisonOptionsDates} from "../common/time.utils";
import {Server} from "@leap4/auth";
import {OneOrgOnly} from "../../shared/OneOrgOnly/OneOrgOnly";

const mapStateToProps = (state: RootState) => ({
    dates: state.dates,
    selectedOrganization:state.auth.selectedOrganizationId
});

const mapActions = {
    setIsLoading,
    setAlert
}

const connector = connect(mapStateToProps, mapActions);

type PropsFromRedux = ConnectedProps<typeof connector>;

export interface IPropsDataPointPage extends PropsFromRedux {
    dates: IDateRangeGState
    setIsLoading: (isLoading: Partial<ILoadingComponents>) => void
    setAlert: (alert: IAlertData) => void
}


const DataPointOverview: React.FC<IPropsDataPointPage> = ({
                                                              dates: {
                                                                  startDate,
                                                                  endDate,
                                                                  isLoading,
                                                                  selectedComparison
                                                              },
                                                              setIsLoading,
                                                              setAlert,
                                                              selectedOrganization
                                                          }): JSX.Element => {

    const [data, setData] = useState<IDataPointOverviewResult>(null);
    const [graphData, setGraphData] = useState<IOverviewGraphResponse>(null)
    const [updatedAt, setUpdatedAt] = useState<Date>(null)
    const [graphInterval, setGraphInterval] = useState<number>(getDefaultTimeDisplay(startDate, endDate))
    const [optionalComparisonDates, setOptionalComparisonDate] = useState<any>(getComparisonOptionsDates(startDate, endDate))
    const [specificComparisonDate, setSpecificComparisonDate] = useState()
    const [comparisonData, setComparisonData] = useState<ComparisonState>({
        compare: selectedComparison,
        data: null
    })
    useEffect(() => {
        if (specificComparisonDate && !checkIfDateIsInComparisonOptions(specificComparisonDate, optionalComparisonDates)) {
            fetchGeneralData()
            fetchGraphData()
        }
    }, [specificComparisonDate])

    useRefreshInterval(()=>{
        if (startDate === endDate && startDate === moment().format("YYYY-MM-DD")) {
            fetchGeneralData()
            fetchGraphData()
        }
    })
    const params = useParams<{ id: string }>()
    const dispatch = useAppDispatch();

    const datapointName = data?.overview?.dataPoint.name

    const fetchGeneralData = () => {
        (async () => {
            try {
                setIsLoading({isLoadingGeneral: true});
                const baseUrl = `${dataPointsUrl}/overview/${params.id}`;
                const apiResponse = await apiClient.post(baseUrl, {
                    startDate,
                    endDate,
                    specificComparisonDate,
                })

                const apiData: IDataPointOverviewResult = apiResponse.data
                setData(apiData)
                if(apiData.lastUpdatedAt) {
                    setUpdatedAt(new Date(apiData.lastUpdatedAt))
                }
            } catch (err) {
                setAlert({msg: "Couldnt fetch data", type: AlertTypes.FAILURE, d: dispatch})
            } finally {
                setIsLoading({isLoadingGeneral: false});
            }
        })()
    };
    const fetchGraphData = () => {
        (async () => {
            try {
                setIsLoading({isLoadingGraph: true});
                const baseUrl = `${dataPointsUrl}/overview/${params.id}/graph`
                const graphResponse = await apiClient.post(baseUrl, {
                    startDate,
                    endDate,
                    specificComparisonDate: specificComparisonDate || null,
                    timeInterval: indexToTimeInterval(graphInterval)
                })
                const currentGraphData: IOverviewGraphResponse = graphResponse.data
                setGraphData(currentGraphData)
            } catch (err) {
                setAlert({msg: "Could'nt fetch Graph Data", type: AlertTypes.FAILURE, d: dispatch})
            } finally {
                setIsLoading({isLoadingGraph: false});
            }
        })()
    }
    const updateTimeInterval = () => {
        const newInterval = getDefaultTimeDisplay(startDate, endDate);
        if (newInterval !== graphInterval) {
            setGraphInterval(getDefaultTimeDisplay(startDate, endDate))
        } else {
            fetchGraphData()
        }
    }
    const updateComparisonData = () => {
        const dataForComparison = data?.overview?.metrics
        let comparisonData;

        switch (selectedComparison) {
            case 1:
                comparisonData = dataForComparison?.dayAgo.totals
                break;
            case 7:
                comparisonData = dataForComparison?.weekAgo.totals
                break;
            case 28:
                comparisonData = dataForComparison?.monthAgo.totals
                break
            case 30:
                comparisonData = dataForComparison?.fullMonthAgo?.totals
                break
            case -1:
                comparisonData = dataForComparison?.specificComparisonDate?.totals
                break;
            default:
                comparisonData = dataForComparison?.selectedDates.totals
                break;
        }
        setComparisonData({compare: selectedComparison, data: comparisonData})
    }


    useEffect(updateTimeInterval, [startDate, endDate,selectedOrganization])
    useEffect(fetchGeneralData, [startDate, endDate, params.id,selectedOrganization])
    useEffect(fetchGraphData, [params.id, graphInterval,selectedOrganization]);
    useEffect(updateComparisonData, [data, selectedComparison,selectedOrganization]);
    const theme = useTheme()


    if (!isLoading.isLoadingGeneral && !data) return (<><ReportHeaderWithProps entityName={datapointName} entityId={params.id}/> <Typography
        variant={"overline"}> Error with getting the
        data...</Typography></>)

    const lastChosenOrganization = localStorage.getItem(Server.headers.X_ORGANZIZATION).split('-')
    if (lastChosenOrganization.length > 1) {
        return <OneOrgOnly />
    }

    return (
        <>
            <ReportHeaderWithProps entityName={datapointName} entityId={params.id}/>
            <Grid container justifyContent={"flex-start"} alignItems={"stretch"}
                  style={{marginRight: "15px", filter: theme.palette.mode === 'dark'? 'drop-shadow(0px 0px 2px lightgray' : 'drop-shadow(0px 0px 5px lightgray', borderRadius: "16px"}}
                  spacing={2}>
                <>
                    <Grid size={{ xs: 12, lg: 4 }} sx={{ flexShrink: 0, paddingLeft: { xs: 0, lg: '12px' } }}>
                        <Grid container direction={"column"} spacing={1} sx={{ height: '100%' }}>
                            <Grid size={12} height={"100%"} display={'flex'} flexDirection={'column'} gap={2} sx={{ minHeight: 0 }}>
                                <Grid size={12} sx={{ flex: 1, minHeight: 0 }}>
                                    <MetricsComparison optionalComparisonDates={optionalComparisonDates} specificComparisonDate={specificComparisonDate}
                                                       setSpecificComparisonDate={setSpecificComparisonDate}/>
                                </Grid>
                                {updatedAt && (
                                    <Grid size={12} sx={{ flex: 0 }}>
                                        <LastUpdatedAt updatedAt={updatedAt} />
                                    </Grid>
                                )}
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid size={{ xs: 12, lg: 8 }} sx={{ flexShrink: 1, minWidth: 0, paddingRight: { xs: 0, lg: '12px' } }}>
                        {!isLoading.isLoadingGeneral && data ?
                            <Paper elevation={5}
                                   style={{
                                       padding: "10px 0",
                                       height: '100%',
                                       margin: '0 0 0 10px',
                                       display: 'flex',
                                       alignItems: 'center',
                                       boxShadow: "none",
                                       borderRadius: "16px"
                                   }}
                            >
                                <Grid item height={"100%"} width={"100%"}>
                                    <OverviewGeneralInfo pageResult={data.overview}
                                                         comparisonData={comparisonData}
                                    />
                                </Grid>
                            </Paper> :
                            <Skeleton sx={{borderRadius: "16px"}} variant={"rounded"} height={"150px"} width={"100%"} animation={"wave"}/>}
                    </Grid>
                    <Grid item sx={{height: '400px', margin: '0 auto', width: {xs: '95%', sm: '96%', md: '99%'}}}>
                        <ComparisonOverviewGraph isLoading={isLoading.isLoadingGraph}
                                                 graphData={graphData}
                                                 startDate={startDate}
                                                 endDate={endDate}
                                                 setNewInterval={setGraphInterval}
                                                 graphIntervalIndx={graphInterval}/>
                    </Grid>
                    <Grid item xs={12} sx={{margin: '0 10px'}}>
                        {!isLoading.isLoadingGeneral && data ?
                            <DataPointTagsTable loading={isLoading.isLoadingGeneral} refreshFunc={fetchGeneralData}
                                                metrics={data.overview.metrics.selectedDates}
                                                compareData={data.overview.metrics[metricsComparisonKeyMap[comparisonData.compare]]?.tags}
                            /> :
                            <Skeleton animation={"wave"} sx={{borderRadius: "16px"}} variant={"rounded"} height={"600px"}/>}
                    </Grid>

                    {isLoading.isLoadingGeneral ?
                        <Skeleton animation={"wave"} sx={{borderRadius: "16px", m:5}} variant={"rounded"} height={"600px"}/> :
                        <Grid item xs={12} sx={{margin: '0 10px'}}>
                            <HomeTopTagsTable tags={data.comboTags.selectedDates || []}
                                              isLoading={isLoading.isLoadingGeneral}
                                              compareData={data.comboTags[metricsComparisonKeyMap[comparisonData.compare]]}


                            />
                        </Grid>}
                    <Grid item sm={12} sx={{height: '400px', margin: '10px auto', width: {xs: '95%', sm: '96%', md: '99%'}}}>
                        <PredictiveGraphContainer url={`${dataPointsUrl}/overview/${params.id}/predictive-graph`} bodyObject={{dataPointId: params.id}} organization={selectedOrganization}/>

                    </Grid>
                </>
            </Grid>
        </>)
}

export default connector(DataPointOverview);
